import Mention from "@tiptap/extension-mention";
import { VueRenderer } from "@tiptap/vue-3";
import type { SuggestionProps, SuggestionKeyDownProps } from "@tiptap/suggestion";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import MentionList from "./MentionList.vue";

export interface MentionItem {
  id: string;
  label: string;
  description: string;
}

export const createMentionExtension = (options: MentionItem[]) =>
  Mention.configure({
    HTMLAttributes: { class: "mention" },
    suggestion: {
      char: "@",
      items: ({ query }: { query: string }): MentionItem[] => {
        return options.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));
      },
      command: ({ editor, range, props }) => {
        const id = props.id as string;
        const textToInsert = `@${id}`;

        // Delete the trigger character and query, then insert plain text
        editor.chain().focus().deleteRange(range).insertContent(textToInsert).run();

        // If the id contains empty parentheses, position cursor inside them
        if (id.endsWith("()")) {
          // Move cursor back by 1 position (before the closing parenthesis)
          editor.commands.setTextSelection(editor.state.selection.from - 1);
        }
      },
      render: () => {
        let component: VueRenderer;
        let popup: TippyInstance[];

        return {
          onStart: (props: SuggestionProps<MentionItem>) => {
            component = new VueRenderer(MentionList, { props, editor: props.editor });
            popup = tippy("body", {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
          },
          onUpdate: (props: SuggestionProps<MentionItem>) => {
            component.updateProps(props);
            popup[0].setProps({ getReferenceClientRect: props.clientRect as () => DOMRect });
          },
          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === "Escape") {
              popup[0].hide();
              return true;
            }
            return component.ref?.onKeyDown(props) ?? false;
          },
          onExit: () => {
            popup[0].destroy();
            component.destroy();
          },
        };
      },
    },
  });
